import { createAsyncThunk, createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit"
import { AppDataState, AppThunkAPIType, unknownError } from "../../../app/appTypes"
import { AppDispatch, RootState, store } from "../../../app/store"
import { getWithAuth, postWithAuth } from "../../../http"

export type Op = 'ADD' | 'DELETE' | 'UPDATE'

export interface DocLink {
    id: number
    documentId: number
    blockKey: string
    companyDocumentTypeId: number
    companyDocumentTypeTitle: string
    number: string
    title: string
    url: string
    notes: string
    modifiedBy: string
    modifyDate: string
}

export interface DocLinkAssignementOp {
    id: number
    operation: Op
    blockKey: string
    companyDocumentTypeId: number,
    number: string,
    title: string,
    url: string,
    notes: string
}

export const loadAllDocLinks = createAsyncThunk<
    DocLink[],
    { docId: number },
    AppThunkAPIType
>("imported-documents/doc-links/load-all",
    async ({ docId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/imported-documents/${docId}/documents-links` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocLink[];
        }

        return rejectWithValue(payload ?? { kind: "unknown" });
    }
);

export const updateDocLink = createAsyncThunk<
    DocLink[],
   { docId: number, items: DocLinkAssignementOp[] },
    AppThunkAPIType
>("imported-documents/doc-links/batch-update", async ({ docId, items }, { dispatch, rejectWithValue }) => {
    const result = await dispatch(
        postWithAuth({
            url: `api/imported-documents/${docId}/document-links/batch`,
            payload: {
                items: items,
            },
        })
    );

    const { payload } = result;

    if (postWithAuth.fulfilled.match(result)) {
        return payload as DocLink[];
    }

    return rejectWithValue(payload ?? { kind: "unknown" });
});

const adapter = createEntityAdapter<DocLink>({
    selectId: a => a.blockKey,
})

interface ImportedDocLinksState {
    docId: number | null
    assignments: EntityState<DocLink>
    loaded: AppDataState
    pendingChanges: Record<string, number | null>
}

const initialState: ImportedDocLinksState = {
    docId: null,
    assignments: adapter.getInitialState(),
    loaded: { type: 'empty' },
    pendingChanges: {},
}

const importedDocLinksSlice = createSlice({
    name: 'importedDocLinks',
    initialState,
    reducers: {
        setDocLinkOnBlock(state, action: PayloadAction<{ blockKey: string; departmentId: number | null }>) {
            const { blockKey, departmentId } = action.payload
            state.pendingChanges[blockKey] = departmentId
        },
    },
    extraReducers: builder => {
        builder.addCase(loadAllDocLinks.pending, (state, action) => {
            state.docId = action.meta.arg.docId
            state.loaded = {
                type: 'loading',
                requestId: action.meta.requestId,
            }
        })
        builder.addCase(loadAllDocLinks.fulfilled, (state, action) => {
            state.docId = action.meta.arg.docId
            state.loaded = { type: 'loaded' }
            adapter.setAll(state.assignments, action.payload)
        })
        builder.addCase(loadAllDocLinks.rejected, (state, action) => {
            state.loaded = {
                type: 'error',
                error: action.payload ?? unknownError(),
            }
        })
        builder.addCase(updateDocLink.fulfilled, (state, action) => {
            adapter.setAll(state.assignments, action.payload)
            state.pendingChanges = {}
        })
    },
})

export const { setDocLinkOnBlock } = importedDocLinksSlice.actions

export const saveDocLinks = async function (docId: number, dispatch: AppDispatch) {
    const state = store.getState() 
    const { pendingChanges, assignments } = state.importedDocLinks
    const ops: DocLinkAssignementOp[] = []

    for (const [blockKey, desiredDocTypeId] of Object.entries(pendingChanges)) {
        const existing = assignments.entities[blockKey]

        if (desiredDocTypeId === null) {
            if (existing) {
                ops.push({
                    operation: 'DELETE',
                    id: existing.id,
                    blockKey,
                    companyDocumentTypeId: existing.companyDocumentTypeId,
                    number: 'dummy',
                    title: '',
                    url: '',
                    notes: '',
                })
            }
        } else if (!existing) {
            ops.push({
                operation: 'ADD',
                id: 0,
                blockKey,
                companyDocumentTypeId: desiredDocTypeId,
                    number: 'dummy',
                    title: '',
                    url: '',
                    notes: '',
            })
        } else if (existing.companyDocumentTypeId !== desiredDocTypeId) {
            ops.push({
                operation: 'UPDATE',
                id: existing.id,
                blockKey,
                companyDocumentTypeId: desiredDocTypeId,
                number: 'dummy',
                title: '',
                url: '',
                notes: '',
            })
        }
    }

    if (ops.length > 0) {
        await dispatch(updateDocLink({ docId, items: ops })).unwrap()
    }
}

const selectState = (state: RootState) => state.importedDocLinks

export const selectDocLinksAssignmentsLoaded = (state: RootState) => state.importedDocLinks.loaded

export const selectHasPendingDeptChanges = (state: RootState) =>
    Object.keys(state.importedDocLinks.pendingChanges).length > 0

export const selectEffectiveDocIdForBlock = (state: RootState, blockKey: string): number | null => {
    const { pendingChanges, assignments } = state.importedDocLinks
    if (blockKey in pendingChanges) {
        return pendingChanges[blockKey]
    }
    const existing = assignments.entities[blockKey]
    return existing?.companyDocumentTypeId ?? null
}

export const {
    selectAll: selectAllDocLinksAssignments,
} = adapter.getSelectors((state: RootState) => selectState(state).assignments)

export default importedDocLinksSlice.reducer

